import { Box, MenuItem, TextField } from "@mui/material";
import React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import {
  type NewSavedMapPlotRequestDetailAdditionalFieldFormData,
  NewSavedMapPlotRequestDetailAdditionalFieldSchema,
} from "@/features/accountSettings/types/schemas";
import { CTextField } from "@/components/TextField/CTextField";
import type { AdditionalFieldType } from "@/types/additionalFields";
import type { AdditionalFieldItem } from "@/features/accountSettings/types/additionalFields";
import { FIELD_TYPE_OPTIONS } from "../constants";
import { validateFieldDuplication } from "../utils/fieldValidation";

interface AddFieldFormProps {
  allFields: AdditionalFieldItem[] | undefined;
  onAdd: (label: string, fieldType: AdditionalFieldType) => void;
  onCancel: () => void;
}

/**
 * 新規フィールド追加フォームコンポーネント
 */
export const AddFieldForm: React.FC<AddFieldFormProps> = ({
  allFields,
  onAdd,
  onCancel,
}) => {
  // 新規項目追加用のフォーム
  const newFieldForm =
    useForm<NewSavedMapPlotRequestDetailAdditionalFieldFormData>({
      mode: "all",
      defaultValues: {
        label: "",
        fieldType: "text",
      },
      resolver: zodResolver(NewSavedMapPlotRequestDetailAdditionalFieldSchema),
    });

  const handleAddField = newFieldForm.handleSubmit(
    (data: NewSavedMapPlotRequestDetailAdditionalFieldFormData) => {
      // 重複バリデーション
      const validationResult = validateFieldDuplication(
        allFields,
        data.label,
        data.fieldType
      );

      if (!validationResult.isValid) {
        newFieldForm.setError("label", {
          type: "manual",
          message: validationResult.errorMessage,
        });
        return;
      }

      // バリデーションOK → 追加処理
      onAdd(data.label, data.fieldType);

      // フォームをリセット
      newFieldForm.reset();
    }
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
          <Box sx={{ minWidth: 200, flex: 1, maxWidth: 300 }}>
            <CTextField
              control={newFieldForm.control}
              name="label"
              label="項目名"
              maxLength={20}
              autoFocus={true}
            />
          </Box>
          <Box sx={{ minWidth: 200, flex: 1, maxWidth: 300 }}>
            <Controller
              name="fieldType"
              control={newFieldForm.control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="型"
                  select
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                >
                  {FIELD_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <SubmitButton
            label="追加"
            variant={ButtonVariantOption.Contained}
            size={SizingWrapperStyle.SMALL}
            timeout={10000}
            onClick={(_event, cancelLoading) => {
              handleAddField().finally(() => {
                cancelLoading();
              });
            }}
          />
          <Button
            label="キャンセル"
            variant={ButtonVariantOption.Outlined}
            size={SizingWrapperStyle.SMALL}
            onClick={onCancel}
          />
        </Box>
      </Box>
    </Box>
  );
};
